import IconClose from "components/Icon/IconClose";
import Image from "next/image";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { trackEventGA, trackEventGTM } from "lib/amplitude";
import { usePreventScroll } from "./usePreventScroll";
import { useAuthStore } from "modules/Auth/authStore";
import { IconNexaText } from "components/Icon/IconNexaText";
import { formatDate } from "lib/formatDate";
import { IconSearch } from "components/Icon/IconSearch";
import IconOpenSidebar from "components/Icon/IconOpenSidebar";
import IconType from "components/Icon/IconType";
import clsx from "clsx";
import IconFullscreen from "components/Icon/IconFullscreen";
import IconMinimizeAvatar from "components/Icon/IconMinimizeAvatar";
import IconSend from "components/Icon/IconSend";

export const AvatarMobileChat = ({
  isReadyToFetch,
  setIsReadyToFetch,
  isNewChatRoomLoading,
  setIsNewChatRoomLoading,
  createChatRoom,
  newChatRoom,
  setNewChatRoom,
  selectedRecapId,
  setSelectedRecapId,
  chatModeTenant,
  nxtxTenant,
  sendInterrupt,
  sendText,
  VisualAudioContext,
  handlePasskeySubmit,
  isCloseWhenLoading,
  setIsCloseWhenLoading,
  keywordChipsData,
  isVisible,
  handleInterrupt,
  dataAvatar,
  isAnyFiltered,
  isKeywordsSearched,
  isScrolled,
  handleMinimize,
  isSmaller,
  mediaStream,
  isMediaReady,
  setIsMediaReady,
  startSession,
  endSession,
  isLoadingSession,
  stream,
  toggleSidebar,
  showSidebar,
  toggleSummarySidebar,
  showSummarySidebar,
  toggleQuizSidebar,
  showQuizSidebar,
  chat,
  lastDisplayedDate,
  currentUser,
  handleCopyText,
  copySuccess,
  chatEndRef,
  summaryRecap,
  summaryEndRef,
  scrollToTop,
  handleBigger,
  handleSmaller,
  volumes,
  isVoiceChatActive,
  isMuted,
  handleCloseVoiceChat,
  handleStartVoiceChat,
  disabledMic,
  micIsLoading,
  modeIsLoading,
  remainingDuration,
  maxDuration,
  formatTime,
  showLimitDesc,
  setShowLimitDesc,
  isRequestSent,
  setIsRequestSent,
  isIncreaseLimit,
  setIsIncreaseLimit,
  limitChecked,
  setLimitChecked,
  showAllSidebar,
  setShowAllSidebar,
  handleFullScreen,
  showToast,
  activeTab,
  setActiveTab,
  handleRequestQuota,
  chatMode,
  isUserTalking,
  textValue,
  setTextValue,
  handleSpeakText,
  isLoadingRepeat,
  handleChangeChatMode,
  saveChat,
  textQuiz,
  setTextQuiz,
  handleSpeakQuiz,
}) => {
  usePreventScroll();

  const router = useRouter();
  const isLMS = router.pathname.includes("/course/[courseSlug]");
  const [showAdditionalContent, setShowAdditionalContent] = useState(true);

  const inputRef = useRef(null);

  const handleFocus = () => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  };

  const handleBlur = () => {
    if (inputRef.current) {
      inputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleSubmit = (isChip?: boolean, textToSubmit?: string) => {
    const textToCheck = isChip ? textToSubmit : textValue;

    if (!textToCheck || textToCheck.trim() === "") {
      return;
    }

    trackEventGTM({
      event: "gaia_text_submit_a",
      property: {
        user_id: currentUser?.id ?? "",
      },
    });

    trackEventGA({
      event: "gaia_text_submit_a",
      property: {
        user_id: currentUser?.id ?? "",
      },
    });

    if (isChip) {
      handleSpeakText(textToSubmit);
    } else {
      handleSpeakText();
    }

    setTextValue("");
  };

  useEffect(() => {
    setShowAdditionalContent(false);
    const timeout = setTimeout(() => {
      setShowAdditionalContent(true);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [isScrolled]);

  const containerRef = useRef(null);
  const videoContainerRef = useRef(null);

  useEffect(() => {
    Promise.all([import("gsap"), import("gsap/ScrollTrigger")]).then(
      ([{ default: gsap }, { ScrollTrigger }]) => {
        gsap.registerPlugin(ScrollTrigger);

        const videoContainer = videoContainerRef.current;

        if (!stream && !isLoadingSession) {
          gsap.set(videoContainer, {
            width: "0px",
            height: "0px",
            bottom: 0,
            right: 0,
            position: "fixed",
          });
        } else if ((stream || isLoadingSession) && isScrolled) {
          gsap.set(videoContainer, {
            width: "100%",
            height: `calc(100vh - ${isVisible ? "64px" : "0px"})`,
            bottom: isVisible ? "64px" : 0,
            right: 0,
            position: "fixed",
          });
        } else {
          gsap.set(videoContainer, {
            width: "47vw",
            height: isSmaller ? "120px" : "30vh",
            bottom: 80,
            right: 10,
            position: "fixed",
          });
        }

        if (stream || isLoadingSession) {
          const tl = gsap.timeline({
            defaults: { duration: 1, ease: "power2.inOut" },
          });

          if (isScrolled) {
            tl.to(videoContainer, {
              width: "47vw",
              height: isSmaller ? "120px" : "30vh",
              bottom: 80,
              right: 10,
              position: "fixed",
            });
          } else {
            tl.to(videoContainer, {
              width: "100%",
              height: `calc(100vh - ${isVisible ? "64px" : "0px"})`,
              bottom: isVisible ? "64px" : 0,
              right: 0,
              zIndex: 30,
              position: "fixed",
            });
          }

          tl.play();

          return () => {
            tl.kill();
          };
        }
      }
    );
  }, [isScrolled, isSmaller, stream, isVisible, isLoadingSession]);

  const [isQuizStarted, setIsQuizStarted] = useState(false);

  const excludedWords = [
    "ketentuan",
    "trigger",
    "skor",
    "grade",
    "passing",
    "penilaian",
    "lolos",
    "gaia",
    "jika anda",
    "memiliki pertanyaan",
  ];

  const includeWords = [":"];

  const filterChatWithQuestion = saveChat.chat.filter((item) => {
    const avatarText = item.avatar?.toLowerCase();

    const containsExcludedWord = excludedWords.some((word) =>
      avatarText.includes(word)
    );

    const containsIncludeWord = includeWords.some((word) =>
      avatarText.includes(word)
    );

    return (
      avatarText.includes("pertanyaan") &&
      containsIncludeWord &&
      !containsExcludedWord
    );
  });

  useEffect(() => {
    if (filterChatWithQuestion.length > 0) {
      setIsQuizStarted(true);
    }
  }, [filterChatWithQuestion.length]);

  useEffect(() => {
    if (!stream) {
      setIsQuizStarted(false);
    }
  }, [stream]);

  const { tenantData } = useAuthStore();

  // Batch Loading
  const [visibleChats, setVisibleChats] = useState(chat?.chat.slice(-20) || []);
  const containerChatRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (chat?.chat) {
      setVisibleChats(chat.chat.slice(-20));
    }
  }, [chat?.chat]);

  const loadMoreVisibleChats = () => {
    if (!chat?.chat || visibleChats.length >= chat.chat.length) return;

    const currentLength = visibleChats.length;
    const newVisibleChats = chat.chat.slice(
      Math.max(0, chat.chat.length - currentLength - 20),
      chat.chat.length - currentLength
    );

    setVisibleChats((prevVisibleChats) => [
      ...newVisibleChats,
      ...prevVisibleChats,
    ]);
  };

  useEffect(() => {
    setTimeout(() => {
      if (containerChatRef.current) {
        containerChatRef.current.scrollTop =
          containerChatRef.current.scrollHeight;
      }
    }, 0);
  }, [chat.chat, isScrolled]);

  const handleChatScroll = () => {
    if (containerChatRef.current?.scrollTop === 0) {
      loadMoreVisibleChats();
    }
  };

  const [visibleRecaps, setVisibleRecaps] = useState(
    summaryRecap?.summary.slice(-10) || []
  );
  const containerSummaryRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (summaryRecap?.summary) {
      setVisibleRecaps(summaryRecap.summary.slice(-10));
    }
  }, [summaryRecap?.summary]);

  const loadMoreVisibleRecaps = () => {
    if (
      !summaryRecap?.summary ||
      visibleRecaps.length >= summaryRecap.summary.length
    )
      return;

    const currentLength = visibleRecaps.length;
    const newVisibleRecaps = summaryRecap.summary.slice(
      Math.max(0, summaryRecap.summary.length - currentLength - 10),
      summaryRecap.summary.length - currentLength
    );

    setVisibleRecaps((prevVisibleRecaps) => [
      ...newVisibleRecaps,
      ...prevVisibleRecaps,
    ]);
  };

  useEffect(() => {
    setTimeout(() => {
      if (containerSummaryRef.current) {
        containerSummaryRef.current.scrollTop =
          containerSummaryRef.current.scrollHeight;
      }
    }, 0);
  }, [summaryRecap.summary, isScrolled]);

  const handleRecapScroll = () => {
    if (containerSummaryRef.current?.scrollTop === 0) {
      loadMoreVisibleRecaps();
    }
  };
  // End Batch Loading

  const [recapSearch, setRecapSearch] = useState("");

  const filteredRecaps = recapSearch
    ? visibleRecaps.filter(
        (item) =>
          (item?.title &&
            item.title.toLowerCase().includes(recapSearch.toLowerCase())) ||
          (item?.date &&
            formatDate(item.date)
              .toLowerCase()
              .includes(recapSearch.toLowerCase()))
      )
    : visibleRecaps;

  const [showSidebarChat, setShowSidebarChat] = useState(false);
  const [pendingCreateChatRoom, setPendingCreateChatRoom] = useState(false);

  useEffect(() => {
    if (!stream) {
      setNewChatRoom(true);
      setSelectedRecapId(null);
    }
  }, [stream]);

  useEffect(() => {
    if (
      pendingCreateChatRoom &&
      chatModeTenant &&
      newChatRoom &&
      isReadyToFetch
    ) {
      createChatRoom();
      setPendingCreateChatRoom(false);
    }
  }, [
    pendingCreateChatRoom,
    isReadyToFetch,
    chatModeTenant,
    newChatRoom,
    createChatRoom,
  ]);

  const sidebarChatAnimate = {
    open: {
      width: "30%",
      transition: {
        damping: 40,
      },
    },
    closed: {
      width: "0px",
      transition: {
        damping: 40,
      },
    },
  };

  const videoClass = `w-full ${
    showSidebar ? "h-[30%] aspect-16/9" : "h-[calc(100vh-64px)]"
  } object-cover transition-all duration-500 ease-in-out`;

  return (
    (stream || isLoadingSession) &&
    !isCloseWhenLoading && (
      <div
        ref={containerRef}
        className={`${
          isScrolled && stream
            ? "fixed w-[10px] h-[10px] z-30"
            : !isScrolled && stream
            ? `fixed w-full h-[calc(100vh-${
                isVisible ? "64px" : "0px"
              })] z-[50]`
            : "fixed w-[10px] h-[10px] z-30"
        } inset-0 transition-all duration-500 ease-in-out mb-8 opacity-100`}
      >
        <div
          ref={videoContainerRef}
          className={`${
            isScrolled && !isSmaller
              ? "fixed bottom-80 right-10"
              : isScrolled && isSmaller
              ? "fixed bottom-80 right-10"
              : `fixed ${
                  isVisible ? "bottom-[64px]" : "bottom-0"
                } w-full h-[calc(100vh-${isVisible ? "64px" : "0px"})] z-[100]`
          } flex items-center justify-center`}
        >
          <div
            className={`flex flex-col w-full h-full relative`}
            style={{
              boxShadow: "4px 4px 12px 0px #0000004D",
            }}
          >
            {isLoadingSession && (
              <div className={`${videoClass} bg-white`}>
                <div
                  className={`${videoClass} flex flex-col justify-center items-center gap-24`}
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(255, 255, 255, 0) 15.5%)",
                  }}
                >
                  <div className="relative">
                    <div className="absolute w-[148px] h-[148px] flex items-center justify-center">
                      <video
                        className="absolute w-100 h-100 object-cover rounded-full"
                        src={`/images/${
                          (tenantData as any)?.setting?.heygen_avatar
                        }.mp4`}
                        loop
                        playsInline
                        autoPlay
                        muted
                      ></video>
                    </div>

                    <motion.div
                      className="relative w-[148px] h-[148px]"
                      animate={{ rotate: 360 }}
                      transition={{
                        repeat: Infinity,
                        duration: 1.5,
                        ease: "linear",
                      }}
                    >
                      <svg className="w-full h-full" viewBox="0 0 100 100">
                        <circle
                          className="stroke-current text-violet_main_to_dark_purple_surface"
                          cx="50"
                          cy="50"
                          r="40"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeDasharray="220"
                          strokeDashoffset="50"
                        />
                      </svg>
                      <motion.div
                        className="absolute w-8 h-8 bg-violet_main_to_dark_purple_surface rounded-full"
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 360 }}
                        transition={{
                          repeat: Infinity,
                          duration: 1.5,
                          ease: "linear",
                        }}
                        style={{ top: "16px", left: "calc(50% - 28px)" }}
                      />
                    </motion.div>
                  </div>

                  <p className="text-b1 font-medium text-[#817E8C]">
                    Memanggil NEXA...
                  </p>
                </div>
              </div>
            )}

            {stream && showAdditionalContent && (
              <div
                className={
                  videoClass +
                  "relative flex justify-center items-center h-full"
                }
                style={{
                  background: "#fff",
                  boxShadow: stream && "4px 4px 12px 0px #0000004D",
                }}
                id="sm-video"
              >
                <>
                  <div className="absolute top-0 left-0 right-0 p-16 border-b border-[#E5E4E7] flex justify-between items-center">
                    <IconNexaText color="#2F0F57" width={60.5} height={20} />

                    <div className="flex items-center gap-12">
                      {/* <div
                        className="cursor-pointer"
                        onClick={isScrolled ? handleFullScreen : handleMinimize}
                      >
                        {isScrolled ? (
                          <IconFullscreen size={20} color="#6B7280" />
                        ) : (
                          <IconMinimizeAvatar size={20} color="#6B7280" />
                        )}
                      </div> */}
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setNewChatRoom(true);
                          setSelectedRecapId(null);
                        }}
                      >
                        <IconType size={20} color="#6B7280" />
                      </div>
                      <div className="cursor-pointer" onClick={endSession}>
                        <IconClose size={20} color="#6B7280" />
                      </div>
                    </div>
                  </div>

                  <div className="pt-52 pb-10 flex flex-col items-center justify-center px-16 w-full h-full">
                    {newChatRoom ? (
                      <p className="text-b1 font-medium text-black mb-6">
                        Ada materi yang mau kamu tanyakan?
                      </p>
                    ) : (
                      <div
                        ref={containerChatRef}
                        onScroll={handleChatScroll}
                        className={`overflow-y-auto hide-scrollbar h-full ${
                          !isScrolled && "w-[730px]"
                        }`}
                      >
                        {chat && (
                          <div className="mb-48">
                            {visibleChats.map((item, index) => {
                              const currentDate = new Date(
                                item?.date
                              ).toDateString();
                              const shouldDisplayDate =
                                currentDate !== lastDisplayedDate;

                              if (shouldDisplayDate) {
                                lastDisplayedDate = currentDate;
                              }

                              return (
                                <div
                                  key={index}
                                  data-index={index}
                                  className="chat-item"
                                >
                                  {shouldDisplayDate && item?.date && (
                                    <div className="flex items-center my-16 opacity-50">
                                      <span className="mx-4 text-b2 font-light text-black">
                                        {formatDate(item?.date as string)}
                                      </span>
                                      <div className="flex-grow border-t border-[#D9D9D9]"></div>
                                    </div>
                                  )}

                                  <div className="mb-12">
                                    {item?.user &&
                                      item?.user != "" &&
                                      item?.user != "-" &&
                                      item?.user.toLowerCase() !=
                                        "mulai quiz" && (
                                        <div className="flex justify-end flex-shrink-0 gap-8 ml-40 mt-12 mb-12">
                                          <div className="p-20 rounded-xl bg-light_neutral10_to_dark_neutral50">
                                            <p
                                              className="text-black font-light text-sm break-all"
                                              dangerouslySetInnerHTML={{
                                                __html: item?.user
                                                  .replace(
                                                    /\*\*(.*?)\*\*/g,
                                                    "<b>$1</b>"
                                                  )
                                                  .replace(
                                                    /#+(.*?)#/g,
                                                    "<b>$1</b>"
                                                  )
                                                  .replace(/\n/g, "<br>"),
                                              }}
                                            />
                                          </div>
                                        </div>
                                      )}

                                    {item?.avatar && item?.avatar != "" && (
                                      <div className="mr-26">
                                        <div className="flex flex-shrink-0 gap-8">
                                          <p
                                            className="text-black font-light text-sm"
                                            dangerouslySetInnerHTML={{
                                              __html: item?.avatar
                                                .replace(
                                                  /\*\*(.*?)\*\*/g,
                                                  "<b>$1</b>"
                                                )
                                                .replace(
                                                  /#+(.*?)#/g,
                                                  "<b>$1</b>"
                                                )
                                                .replace(/\n/g, "<br>"),
                                            }}
                                          />
                                        </div>
                                        <div className="cursor-pointer hover:bg-light_neutral10_to_dark_neutral50 mt-8 flex-shrink-0 rounded-full w-32 h-32 p-4 flex items-center justify-center">
                                          <Image
                                            onClick={(e) =>
                                              handleCopyText(
                                                e,
                                                item?.avatar,
                                                index
                                              )
                                            }
                                            src={
                                              copySuccess[index]
                                                ? "/images/copy-white.svg"
                                                : "/images/copy-white.svg"
                                            }
                                            alt="gaia"
                                            width={16}
                                            height={16}
                                            className="rounded-full"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                            {keywordChipsData.length > 0 && (
                              <div
                                className={`flex flex-col ml-38 gap-8 w-fit`}
                              >
                                {keywordChipsData.map((item, index) => (
                                  <div
                                    key={index}
                                    onClick={() => {
                                      handleInterrupt();
                                      handleSubmit(true, item);
                                    }}
                                    className="rounded-full border border-neutral-50 py-8 px-12 cursor-pointer"
                                    style={{
                                      boxShadow: "4px 4px 12px 0px #00000016",
                                    }}
                                  >
                                    <p className="text-neutral-80 text-c1 font-light">
                                      {item}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="w-full mb-10">
                      <div
                        className={`bg-white py-12 flex ${
                          isScrolled ? "justify-between" : "justify-center"
                        } items-center z-50`}
                      >
                        <div
                          className={`relative ${
                            isScrolled ? "w-full" : "w-[730px]"
                          } flex justify-between`}
                        >
                          <textarea
                            autoFocus
                            placeholder="Tanyakan apapun disini..."
                            value={textValue}
                            onChange={(e) => setTextValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (nxtxTenant) {
                                  sendInterrupt();
                                  sendText(textValue);

                                  if (chatModeTenant && newChatRoom) {
                                    if (isReadyToFetch) {
                                      createChatRoom();
                                    } else {
                                      setPendingCreateChatRoom(true);
                                    }
                                  }
                                } else {
                                  handleInterrupt();
                                  handleSubmit();
                                }
                              }
                            }}
                            disabled={!stream || isNewChatRoomLoading}
                            rows={4}
                            className={clsx(
                              "w-full pl-12 py-12 bg-field_input pr-40 text-b2 text-neutral-80 font-light rounded-2xl ring-1 ring-[#E5E7EB] focus:ring-violet_main_to_dark_purple_surface focus:outline-none resize-none",
                              isLoadingRepeat && "pr-10"
                            )}
                            style={{ minHeight: "35px", maxHeight: "120px" }}
                          />

                          <div className="flex justify-end items-center gap-8 absolute bottom-10 right-10">
                            {/* <div className="rounded-full flex items-center gap-2 border border-light_neutral20_to_dark_neutral60">
                              <video
                                className="rounded-full w-28 h-28 object-cover m-2"
                                src={`/images/${
                                  (tenantData as any)?.setting?.heygen_avatar
                                }.mp4`}
                                loop
                                playsInline
                                autoPlay
                              ></video>

                              <p className="text-dark_purple-100 text-c1 font-light mr-8">
                                Avatar Mode
                              </p>
                            </div> */}

                            <div
                              onClick={(e) => {
                                e.stopPropagation();

                                if (isNewChatRoomLoading) return;

                                if (nxtxTenant) {
                                  sendInterrupt();
                                  sendText(textValue);

                                  if (chatModeTenant && newChatRoom) {
                                    if (isReadyToFetch) {
                                      createChatRoom();
                                    } else {
                                      setPendingCreateChatRoom(true);
                                    }
                                  }
                                } else {
                                  handleInterrupt();
                                  handleSubmit();
                                }
                              }}
                              className={`p-8 rounded-full cursor-pointer ${
                                isNewChatRoomLoading ? "cursor-not-allowed" : ""
                              } ${
                                textValue.length > 0
                                  ? "bg-violet_main_to_dark_purple_surface"
                                  : "bg-light_neutral20_to_dark_neutral60"
                              }`}
                            >
                              <IconSend size={20} color="#FFFFFF" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="absolute bottom-10 font-light text-c2 text-center text-[#000000CC]">
                      Interaksi dengan NEXA terekam & hanya sebagai referensi.{" "}
                      <span
                        className="underline cursor-pointer"
                        onClick={() => window.open("/terms-of-use", "_blank")}
                      >
                        Terms
                      </span>
                    </p>
                  </div>
                </>

                <motion.div
                  variants={sidebarChatAnimate}
                  animate={showSidebarChat ? "open" : "closed"}
                  className={`flex relative h-full bg-light_neutral10_to_dark_neutral50`}
                >
                  <div className="min-h-54 absolute top-0 left-0 right-0 py-8 px-16 border-b border-[#E5E4E7] bg-light_neutral10_to_dark_neutral50 flex justify-between items-center gap-14">
                    <div className="flex items-center gap-12">
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          setNewChatRoom(true);
                          setSelectedRecapId(null);
                        }}
                      >
                        <IconType size={20} color="#6B7280" />
                      </div>
                    </div>

                    <div className="w-full relative flex justify-between items-center">
                      <input
                        autoFocus
                        placeholder="Cari disini"
                        value={recapSearch}
                        onChange={(e) => setRecapSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={!stream}
                        className={clsx(
                          "h-32 w-full pl-12 py-12 bg-field_input pr-40 text-b2 text-neutral-80 font-light rounded-2xl ring-1 ring-[#6B7280] focus:ring-violet_main_to_dark_purple_surface focus:outline-none resize-none",
                          isLoadingRepeat && "pr-10"
                        )}
                      />

                      <div
                        onClick={(e) => {
                          e.stopPropagation();

                          if (recapSearch.length > 0) {
                            setRecapSearch("");
                          }
                        }}
                        className={`absolute right-0 p-8 rounded-br-2xl rounded-tr-2xl cursor-pointer bg-light_neutral20_to_dark_neutral60`}
                      >
                        {recapSearch.length > 0 ? (
                          <IconClose size={16} color="#883CFF" />
                        ) : (
                          <IconSearch size={16} color="#883CFF" />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-12">
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSidebarChat(!showSidebarChat);
                        }}
                        className="cursor-pointer"
                      >
                        <IconOpenSidebar size={20} color="#6B7280" />
                      </div>
                    </div>
                  </div>

                  <div
                    ref={containerSummaryRef}
                    onScroll={handleRecapScroll}
                    className="pt-64 pb-10 flex flex-col px-16 w-full h-full overflow-y-auto hide-scrollbar"
                  >
                    {filteredRecaps
                      ?.slice()
                      ?.reverse()
                      ?.map((item, index) => {
                        const dateOnly = item?.date
                          ? item.date.split("T")[0]
                          : "";
                        const shouldDisplayDate =
                          dateOnly !== lastDisplayedDate;

                        if (shouldDisplayDate) {
                          lastDisplayedDate = dateOnly;
                        }

                        return (
                          <div
                            className="py-12 cursor-pointer"
                            onClick={() => {
                              setNewChatRoom(false);
                              setSelectedRecapId(item?.session_id);
                            }}
                          >
                            {shouldDisplayDate && item?.date && (
                              <p className="text-c1 font-light text-light_neutral-40 mb-4">
                                {formatDate(item?.date as string)}
                              </p>
                            )}

                            <div
                              className={clsx(
                                selectedRecapId === item?.session_id &&
                                  "p-8 bg-light_neutral20_to_dark_neutral60 rounded-2xl"
                              )}
                            >
                              <p
                                className={`${
                                  selectedRecapId !== item?.session_id && "px-8"
                                } text-dark_purple-100 font-light text-b2`}
                              >
                                {item?.title}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
};
